import { useEffect, useState } from 'react';
import { probeRoom, type RoomProbe } from '@/net/session';

/**
 * Ask the relay about a room on mount and every `intervalMs` while the page
 * is visible. `undefined` = not answered yet; `null` = relay unreachable.
 */
export function useRoomProbe(
  code: string | null,
  intervalMs = 5000
): RoomProbe | null | undefined {
  const [probe, setProbe] = useState<RoomProbe | null | undefined>(undefined);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    let timer = 0;
    const schedule = () => {
      timer = window.setTimeout(() => void tick(), intervalMs);
    };
    const tick = async () => {
      if (!alive) return;
      if (document.visibilityState === 'hidden') {
        schedule();
        return;
      }
      const r = await probeRoom(code).catch(() => null);
      if (!alive) return;
      setProbe(r);
      schedule();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        window.clearTimeout(timer);
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    void tick();
    return () => {
      alive = false;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [code, intervalMs]);

  return probe;
}

export interface ProbeSummary {
  label: string;
  tone: 'ok' | 'warn' | 'bad' | 'muted';
  /** The room cannot be joined or reopened. */
  gone: boolean;
}

export function summarizeProbe(
  probe: RoomProbe | null | undefined
): ProbeSummary {
  if (probe === undefined)
    return { label: 'Checking the room…', tone: 'muted', gone: false };
  if (probe === null)
    return { label: "Can't reach the relay", tone: 'warn', gone: false };
  if (!probe.exists)
    return { label: 'Room not found', tone: 'bad', gone: true };
  if (probe.ended) return { label: 'Room ended', tone: 'bad', gone: true };
  if (probe.hostPresent)
    return { label: 'Host online', tone: 'ok', gone: false };
  return { label: 'Host away', tone: 'warn', gone: false };
}
